'use client'

import { Label, Image } from '@macarongfactory/web-ui'
import { useRef } from 'react'

import { OptimizedImage } from '@/shared/ui'

import { formatNowPromotionPeriod, getNowCategoryLabelVariant } from '../lib'
import { carStoryTracking } from '../lib/tracking'
import { type IMycleContentListItem } from '../model'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface INowContentsSectionProps {
  items: IMycleContentListItem[]
}

const NowContentsSection = ({ items }: INowContentsSectionProps) => {
  if (items.length === 0) return null

  return (
    <section className='pt-dimension-x5 pb-dimension-x10'>
      <ul className='gap-y-dimension-x4 gap-x-dimension-x3 px-dimension-x5 grid grid-cols-2'>
        {items.map(item => (
          <li key={item.serverId}>
            <NowContentCard item={item} />
          </li>
        ))}
      </ul>
    </section>
  )
}

const NowContentCard = ({ item }: { item: IMycleContentListItem }) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null)
  useTrackOnVisible({
    ref: cardRef,
    tracker: () => carStoryTracking.showNowContentCard(item.title),
  })

  const period = formatNowPromotionPeriod(
    item.promotionStartDateTime,
    item.promotionEndDateTime
  )

  // NOW 콘텐츠는 항상 어드민에서 발행된 외부 이벤트 페이지(contentUrl)로 직접 이동
  return (
    <a
      ref={cardRef}
      href={item.contentUrl ?? '#'}
      onClick={() => carStoryTracking.clickNowContentCard(item.title)}
      className='gap-y-dimension-x2-5 flex flex-col'
    >
      <div className='bg-background-neutral-weak rounded-dimension-x3 relative aspect-square w-full overflow-hidden'>
        <OptimizedImage
          src={item.thumbnailUrl}
          alt={item.title}
          width='100%'
          height='100%'
          radius={0}
          scaletype='crop'
          sizes='(max-width: 480px) 50vw, 240px'
          fallback={<Image.Fallback />}
        />
      </div>
      <div className='gap-y-dimension-x2 flex flex-col items-start'>
        <div className='gap-y-dimension-x1 flex flex-col'>
          <p className='text-title-2-medium text-foreground-neutral-strong line-clamp-2 whitespace-pre-line'>
            {item.title}
          </p>
          {period && (
            <p className='text-caption-1-medium text-foreground-neutral-assistive'>
              {period}
            </p>
          )}
        </div>
        {item.categoryNames.length > 0 && (
          <div className='gap-dimension-x1 flex flex-wrap'>
            {item.categoryNames.map(categoryName => (
              <Label
                key={categoryName}
                text={categoryName}
                type='weak'
                variant={getNowCategoryLabelVariant(categoryName)}
                size='small'
              />
            ))}
          </div>
        )}
      </div>
    </a>
  )
}

export default NowContentsSection
